// services/thermalPrinterService.js

class ThermalPrinterService {
    constructor() {
        this.encoder = null;
        this.device = null;
        this.characteristic = null;
    }

    // Connexion via Web Bluetooth (imprimantes BT)
    async connectBluetooth() {
        try {
            this.device = await navigator.bluetooth.requestDevice({
                filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
            });

            const server = await this.device.gatt.connect();
            const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
            this.characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

            return true;
        } catch (error) {
            console.error('Erreur connexion Bluetooth:', error);
            throw error;
        }
    }

    // Connexion via Web USB
    async connectUSB() {
        try {
            this.device = await navigator.usb.requestDevice({
                filters: [
                    { vendorId: 0x0483 }, // STM
                    { vendorId: 0x0416 }, // Winbond
                    { vendorId: 0x04b8 }, // Epson
                    { vendorId: 0x0519 }  // Star
                ]
            });

            await this.device.open();
            await this.device.selectConfiguration(1);
            await this.device.claimInterface(0);

            return true;
        } catch (error) {
            console.error('Erreur connexion USB:', error);
            throw error;
        }
    }

    // Génère les commandes ESC/POS
    generateTicketCommands(sale, shopInfo = {}) {
        const commands = [];

        // Initialisation
        commands.push([0x1B, 0x40]); // ESC @ - Reset
        commands.push([0x1B, 0x61, 0x01]); // Centrer

        // En-tête boutique
        commands.push([0x1D, 0x21, 0x11]); // Double hauteur/largeur
        commands.push(this.textToBytes(shopInfo.name || 'MA BOUTIQUE'));
        commands.push([0x0A]);
        commands.push([0x1D, 0x21, 0x00]); // Taille normale

        if (shopInfo.address) {
            commands.push(this.textToBytes(shopInfo.address));
            commands.push([0x0A]);
        }
        if (shopInfo.phone) {
            commands.push(this.textToBytes(`Tel: ${shopInfo.phone}`));
            commands.push([0x0A]);
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Infos ticket
        commands.push([0x1B, 0x61, 0x00]); // Aligner gauche
        commands.push(this.textToBytes(`Ticket: ${sale.invoice_number}`));
        commands.push([0x0A]);
        commands.push(this.textToBytes(`Date: ${this.formatDate(sale.sale_date)}`));
        commands.push([0x0A]);

        if (sale.user?.name) {
            commands.push(this.textToBytes(`Vendeur: ${sale.user.name}`));
            commands.push([0x0A]);
        }

        if (sale.customer?.name) {
            commands.push(this.textToBytes(`Client: ${sale.customer.name}`));
            commands.push([0x0A]);
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Articles
        if (sale.sale_items && sale.sale_items.length > 0) {
            for (const item of sale.sale_items) {
                console.log(item)
                const name = this.truncate(item.product.name, 30);
                const qty = item.quantity;
                const price = this.formatAmount(item.unit_price);
                const total = this.formatAmount(item.total_amount);

                commands.push(this.textToBytes(name));
                commands.push([0x0A]);
                commands.push(this.textToBytes(`  ${qty} x ${price} = ${total}`));
                commands.push([0x0A]);
            }
        }

        // Ligne séparatrice
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);

        // Totaux
        commands.push([0x1B, 0x61, 0x02]); // Aligner droite

        if (parseFloat(sale.discount) > 0) {
            commands.push(this.textToBytes(`Remise: -${this.formatAmount(sale.discount)}`));
            commands.push([0x0A]);
        }

        // Total en gras
        commands.push([0x1B, 0x45, 0x01]); // Gras ON
        commands.push([0x1D, 0x21, 0x01]); // Double hauteur
        commands.push(this.textToBytes(`TOTAL: ${this.formatAmount(sale.total_incl_tax)}`));
        commands.push([0x0A]);
        commands.push([0x1D, 0x21, 0x00]); // Taille normale
        commands.push([0x1B, 0x45, 0x00]); // Gras OFF

        // Mode de paiement
        if (sale.payment) {
            commands.push([0x1B, 0x61, 0x00]); // Aligner gauche
            commands.push(this.textToBytes(`Paiement: ${this.getPaymentMethod(sale.payment.payment_type)}`));
            commands.push([0x0A]);
            commands.push(this.textToBytes(`Montant recu: ${this.formatAmount(sale.payment.amount)}`));
            commands.push([0x0A]);
        }

        // Pied de page
        commands.push(this.textToBytes('--------------------------------'));
        commands.push([0x0A]);
        commands.push([0x1B, 0x61, 0x01]); // Centrer
        commands.push(this.textToBytes('Merci de votre visite !'));
        commands.push([0x0A]);
        commands.push([0x0A]);

        // Coupe papier
        commands.push([0x1D, 0x56, 0x41, 0x03]); // GS V A - Coupe partielle

        return commands;
    }

    // Impression via Bluetooth
    async printBluetooth(commands) {
        const data = this.flattenCommands(commands);
        const chunkSize = 20;

        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            await this.characteristic.writeValue(new Uint8Array(chunk));
            await this.delay(50);
        }
    }

    // Impression via USB
    async printUSB(commands) {
        const data = new Uint8Array(this.flattenCommands(commands));
        await this.device.transferOut(1, data);
    }

    // Impression via serveur (réseau/série)
    async printViaServer(sale, printerConfig = {}) {
        const response = await fetch('/api/print/ticket', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
                sale_id: sale.id,
                printer_ip: printerConfig.ip,
                printer_port: printerConfig.port || 9100,
                printer_type: printerConfig.type || 'network'
            })
        });

        if (!response.ok) {
            throw new Error('Erreur impression serveur');
        }

        return response.json();
    }

    // Méthode principale d'impression
    async printTicket(sale, options = {}) {
        const {
            connectionType = 'server', // 'bluetooth', 'usb', 'server'
            shopInfo = {},
            printerConfig = {}
        } = options;

        try {
            switch (connectionType) {
                case 'bluetooth': {
                    if (!this.characteristic) {
                        await this.connectBluetooth();
                    }
                    const btCommands = this.generateTicketCommands(sale, shopInfo);
                    await this.printBluetooth(btCommands);
                    break;
                }

                case 'usb': {
                    if (!this.device) {
                        await this.connectUSB();
                    }
                    const usbCommands = this.generateTicketCommands(sale, shopInfo);
                    await this.printUSB(usbCommands);
                    break;
                }

                case 'server':
                default:
                    await this.printViaServer(sale, printerConfig);
                    break;
            }

            return { success: true, message: 'Ticket imprimé avec succès' };
        } catch (error) {
            console.error('Erreur impression:', error);
            throw error;
        }
    }

    // Utilitaires
    textToBytes(text) {
        const encoder = new TextEncoder();
        return Array.from(encoder.encode(text));
    }

    flattenCommands(commands) {
        return commands.flat();
    }

    truncate(str, length) {
        if (str.length <= length) return str;
        return str.substring(0, length - 3) + '...';
    }

    formatAmount(amount) {
        const num = Math.round(amount || 0);
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' F';
    }

    formatDate(dateString) {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getPaymentMethod(type) {
        const methods = {
            'cash': 'Espèces',
            'card': 'Carte',
            'mobile_money': 'Mobile Money',
            'orange_money': 'Orange Money',
            'wave': 'Wave',
            'check': 'Chèque',
            'bank_transfer': 'Virement',
            'other': 'Autre'
        };
        return methods[type] || type;
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Déconnexion
    disconnect() {
        if (this.device?.gatt?.connected) {
            this.device.gatt.disconnect();
        }
        this.device = null;
        this.characteristic = null;
    }
}

export default new ThermalPrinterService();